import React, { useRef, useState } from 'react';
import theme from './LandingPageSetupMilestones.module.scss';
import mainTheme from '../LandingPageSetup.module.scss';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { faGripDots } from '@fortawesome/pro-solid-svg-icons';
import { faPlus, faTrashAlt } from '@fortawesome/pro-light-svg-icons';
import { ValidatingInput, ValidationForm, ValidationSummary } from '@/components/forms/validation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAsyncState } from '@/utils/hooks';
import { ProjectMilestone } from '@/types';
import { getLandingPageMilestones, setLandingPageMilestones } from '@/services/landing-page';
import { LoadingIndicator, Sortable, Toast, arrayMove } from '@ygtechnologies/bygr-frontend-component-library';
import { AlertBanner, Button, ButtonBar, Checkbox, ConfirmButton, MilestoneTimeline } from '@ygtechnologies/rubus-ui';

interface LandingPageSetupMilestonesProps {
    projectId: number;
    constructionPhaseId: number | null;
}
const MilestoneTitleMaxLength = 25;
const MilestoneDescriptionMaxLength = 30;

interface SortableListProps {
    setMilestones(m: ProjectMilestone[]): void;
    milestones: ProjectMilestone[];
}
interface LandingPageSetupMilestoneProps extends SortableListProps {
    m: ProjectMilestone;
    handleProps: object;
}
const SortableMilestone: React.FC<LandingPageSetupMilestoneProps> = ({ m, setMilestones, milestones, handleProps }) => (
    <div className={theme.row}>
        <div className={theme.dragHandle} {...handleProps}>
            <FontAwesomeIcon icon={faGripDots} rotation={90} />
        </div>
        <div className={theme.inputSection}>
            <div className={theme.topRow}>
                <ValidatingInput
                    type="text"
                    required
                    maxLength={MilestoneTitleMaxLength}
                    className="form-control"
                    placeholder={t`Milestone title (max 25 characters)`}
                    value={m.label}
                    onChange={(e) =>
                        setMilestones(milestones.map((_m, i) => (m.id === _m.id ? { ...m, label: e.target.value } : _m)))
                    }
                />
                <Checkbox
                    inputSize="md"
                    className={theme.checkboxLabel}
                    checked={m.isCompleted}
                    onChange={() =>
                        setMilestones(
                            milestones.map((_m, i) => (m.id === _m.id ? { ...m, isCompleted: !m.isCompleted } : _m))
                        )
                    }>
                    <Trans>Milestone marked</Trans>
                </Checkbox>
            </div>
            <ValidatingInput
                type="text"
                className="form-control"
                maxLength={MilestoneDescriptionMaxLength}
                placeholder={t`Milestone description (max 30 characters)`}
                value={m.description}
                onChange={(e) =>
                    setMilestones(milestones.map((_m, i) => (m.id === _m.id ? { ...m, description: e.target.value } : _m)))
                }
            />
        </div>
        <ConfirmButton
            dialogTitle={<Trans>Delete milestone</Trans>}
            dialogBody={<Trans>Are you sure you want to delete this milestone?</Trans>}
            onConfirm={() => setMilestones(milestones.filter((_m) => m.id !== _m.id))}
            variant="tertiary"
            confirmButton={{
                variant: 'danger',
                children: <Trans>Remove</Trans>,
            }}
            className={theme.trashButton}>
            <FontAwesomeIcon size="lg" icon={faTrashAlt} />
        </ConfirmButton>
    </div>
);

const LandingPageSetupMilestones: React.FC<LandingPageSetupMilestonesProps> = ({ projectId, constructionPhaseId }) => {
    const [milestones, setMilestones] = useAsyncState(
        () => getLandingPageMilestones(projectId, constructionPhaseId),
        [projectId, constructionPhaseId],
        null
    );
    const [isSaving, setIsSaving] = useState(false);
    const newItemCounter = useRef(0);

    if (!milestones) {
        return <LoadingIndicator />;
    }
    const onSave = async () => {
        setIsSaving(true);
        try {
            const removedWrongId = milestones.map((m) => ({ ...m, id: m.id < 0 ? undefined : m.id }));
            const newMilestones = await setLandingPageMilestones(projectId, removedWrongId, constructionPhaseId);
            Toast.success(<Trans>Saved milestones</Trans>);
            setMilestones(newMilestones);
        } finally {
            setIsSaving(false);
        }
    };

    const addMilestone = () => {
        setMilestones(
            milestones.concat([
                {
                    isCompleted: false,
                    label: '',
                    description: '',
                    sortOrder: milestones.length,
                    id: --newItemCounter.current,
                },
            ])
        );
    };

    const onSortEnd = ({ oldIndex, newIndex }) => {
        const newArray = arrayMove(milestones, oldIndex, newIndex);
        setMilestones(newArray);
    };
    return (
        <>
            <AlertBanner type="info">
                <Trans id="landing_page_milestones_help_banner">
                    Add milestones to show the progress of your project. Each milestone should have a short title and
                    description. You can mark milestones as completed, reorder them by dragging, and remove or add new ones
                    as needed. These milestones will be visible to users under the `News` page on the landing page.
                </Trans>
            </AlertBanner>
            <ValidationForm onSubmit={onSave} className={mainTheme.section}>
                {milestones.length > 0 && (
                    <>
                        <div className={theme.timeline}>
                            <MilestoneTimeline>
                                {milestones.map((m) => (
                                    <MilestoneTimeline.Step key={m.id} {...m} />
                                ))}
                            </MilestoneTimeline>
                        </div>
                        <Sortable items={milestones} idField="id" onSortEnd={onSortEnd} direction="vertical">
                            {(m, { handleProps, isDragging }) => (
                                <SortableMilestone
                                    handleProps={handleProps}
                                    milestones={milestones}
                                    setMilestones={setMilestones}
                                    m={m}
                                />
                            )}
                        </Sortable>
                    </>
                )}
                <ValidationSummary />
                <ButtonBar>
                    <Button icon={faPlus} variant="tertiary" onClick={addMilestone}>
                        <Trans>Add milestone</Trans>
                    </Button>
                    <ButtonBar.Right>
                        <Button className={mainTheme.saveButton} type="submit" variant="primary" loading={isSaving}>
                            <Trans>Save</Trans>
                        </Button>
                    </ButtonBar.Right>
                </ButtonBar>
            </ValidationForm>
        </>
    );
};

export default LandingPageSetupMilestones;
